import { IDriver } from "@app/interfaces/IDriver";
import { ITag, ITagDriverMapping, ITagDriverMappingQueryOptions, ITagGeofenceMapping, ITagGeofenceMappingQueryOptions, ITagQueryOptions, ITagVehicleMapping, ITagVehicleMappingQueryOptions } from "@app/interfaces/ITag";
import { IVehicle } from "@app/interfaces/IVehicle";
import { Colour, IGeofence } from "@interfaces/IGeofences";
import { CacheService } from '@services/cache.Service';
import * as angular from "angular";
import { copy } from "angular";
import { ColourService } from "./colour.service";

export enum TagResourceType {
    Vehicle,
    Driver,
    Geofence
}

export type TagResourceMapping = ITagVehicleMapping | ITagDriverMapping | ITagGeofenceMapping;
export type TagResource = IVehicle | IDriver | IGeofence;
export type TagMappingQueryOptions = ITagVehicleMappingQueryOptions | ITagDriverMappingQueryOptions | ITagGeofenceMappingQueryOptions;

export class TagsService {

    tagsServiceCache = "tagsServiceCache";

    static $inject = ["$q", "$http", "colourService", "cacheService"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly $http: angular.IHttpService,
        private readonly colourService: ColourService,
        private readonly cacheService: CacheService) {
        this.cacheService.createCache(this.tagsServiceCache, { maxAge: 5 * 60 * 1000 });
    }

    postTag(tag: ITag): angular.IPromise<ITag> {
        return this.$http({
            url: "/api/tags",
            method: "POST",
            data: tag
        }).then((result: angular.IHttpPromiseCallbackArg<ITag>) => {
            this.cacheService.addOrUpdateItem(this.tagsServiceCache, tag, (v1, v2) => v1.Id == v2.Id);

            return result.data;
        });
    }

    getTags(queryOptions: ITagQueryOptions = {}): angular.IPromise<ITag[]> {

        queryOptions.Active = true;

        return this.cacheService.getOrRetrieveData(this.tagsServiceCache, () => this.getTagsFromDatabase(queryOptions), queryOptions?.invalidateCache);
    }

    getTagsFromDatabase(queryOptions: ITagQueryOptions = {}): angular.IPromise<ITag[]> {
        return this
            .$http({
                url: "/api/tags",
                method: "GET",
                cache: false,
                params: queryOptions
            })
            .then((result: angular.IHttpPromiseCallbackArg<ITag[]>) => {
                result.data.forEach(tag => {
                    if (tag.Colour === Colour.None) {
                        return;
                    }

                    this.colourService.tagColours.forEach(colour => {
                        if (tag.Colour === colour.colourEnum) {
                            tag.ColourObject = colour;
                        }
                    });
                });

                return result.data;
            })
            .catch(() => {
                return [];
            });
    }

    getTag(tagId: number, invalidateCache: boolean = false): angular.IPromise<ITag> {
        return this
            .$http({
                url: `/api/tags/mappings/vehicle/${tagId}`,
                method: "GET",
                params: { invalidateCache }
            })
            .then((result: angular.IHttpPromiseCallbackArg<ITag>) => {
                return result.data;
            });
    }

    getTagVehicleMappings(queryOptions: ITagVehicleMappingQueryOptions = {}, invalidateCache?: boolean): angular.IPromise<ITagVehicleMapping[]> {
        return this
            .$http({
                url: "/api/tags/mappings/vehicle",
                method: "GET",
                cache: invalidateCache,
                params: { invalidateCache, ...queryOptions }
            })
            .then((result: angular.IHttpPromiseCallbackArg<ITagVehicleMapping[]>) => {
                return result.data;
            })
            .catch(() => {
                return [];
            });
    }

    getTagDriverMappings(queryOptions: ITagDriverMappingQueryOptions = {}, invalidateCache: boolean = false): angular.IPromise<ITagDriverMapping[]> {
        return this
            .$http({
                url: "/api/tags/mappings/driver",
                method: "GET",
                cache: invalidateCache,
                params: { invalidateCache, ...queryOptions }
            })
            .then((result: angular.IHttpPromiseCallbackArg<ITagDriverMapping[]>) => {
                return result.data;
            })
            .catch(() => {
                return [];
            });
    }

    getTagGeofenceMappings(queryOptions: ITagGeofenceMappingQueryOptions = {}, invalidateCache: boolean = false): angular.IPromise<ITagGeofenceMapping[]> {
        return this
            .$http({
                url: "/api/tags/mappings/geofence",
                method: "GET",
                cache: invalidateCache,
                params: { invalidateCache, ...queryOptions }
            })
            .then((result: angular.IHttpPromiseCallbackArg<ITagGeofenceMapping[]>) => {
                return result.data;
            })
            .catch(() => {
                return [];
            });
    }

    addTagMapping(mapping: TagResourceMapping, mappingType: TagResourceType): angular.IPromise<TagResourceMapping> {

        const endpoint = TagResourceType[mappingType].toLowerCase();

        return this.$http({
            url: `/api/tags/mappings/${endpoint}`,
            method: "POST",
            data: mapping
        }).then((result: angular.IHttpPromiseCallbackArg<TagResourceMapping>) => {
            return result.data;
        });
    }

    deleteTagMapping(mapping: TagResourceMapping, mappingType: TagResourceType): angular.IPromise<any> {

        const endpoint = TagResourceType[mappingType].toLowerCase();

        return this.$http({
            url: `/api/tags/mappings/${endpoint}/${mapping.Id}`,
            method: "DELETE"
        });
    }

    saveResourceTags(resource: TagResource, resourceType: TagResourceType): angular.IPromise<void> {

        if (!resource) {
            console.warn("No resource, returning");
            resource.TagIds = [];

            return this.$q.resolve();
        }

        const resourceProperty: string = this.getMappingPropertyForResourceType(resourceType);
        const pendingTagIds: number[] = (resource.TagIds?.length) ? copy(resource.TagIds) : [];

        const queryOptions: TagMappingQueryOptions = {};

        queryOptions[resourceProperty] = resource.Id;

        let tagMappingsPromise;

        if (resourceType === TagResourceType.Driver) {
            tagMappingsPromise = this.getTagDriverMappings(queryOptions)

        } else if (resourceType === TagResourceType.Geofence) {
            tagMappingsPromise = this.getTagGeofenceMappings(queryOptions)

        } else if (resourceType === TagResourceType.Vehicle) {
            tagMappingsPromise = this.getTagVehicleMappings(queryOptions)
        }

        let existingTagMappings: TagResourceMapping[] = [];

        return tagMappingsPromise.then(response => {

            existingTagMappings = [...response];

            // find the difference
            const tagIdsToAdd: number[] = pendingTagIds.filter(pendingTagId => !existingTagMappings.find(existingTagMapping => existingTagMapping.TagId === pendingTagId));
            const tagMappingsToRemove: TagResourceMapping[] = existingTagMappings.filter(existingTagMapping => !pendingTagIds.find(pendingTagId => pendingTagId === existingTagMapping.TagId));

            const removePromises: any[] = [];
            const addPromises: any[] = [];


            tagIdsToAdd?.forEach(newTag => {
                const newTagMapping: TagResourceMapping = {
                    Id: 0,
                    TagId: newTag
                };

                newTagMapping[resourceProperty] = resource.Id;

                const newTagMappingPromise = this.addTagMapping(newTagMapping, resourceType);

                addPromises.push(newTagMappingPromise);
            });

            tagMappingsToRemove?.forEach(mappingToRemove => {
                const newTagMappingPromise = this.deleteTagMapping(mappingToRemove, resourceType);

                removePromises.push(newTagMappingPromise);
            });

            return this.$q.all([...removePromises, ...addPromises]).then(() => {
                resource.TagIds = [...pendingTagIds];
            });
        });
    }

    mapTagMappingsToResources(mappings: TagResourceMapping[], resources: TagResource[], resourceType: TagResourceType): void {
        const resourcesMap = new Map<number, TagResource>(resources.map(r => [r.Id, r]));

        if (!mappings?.length || !resources?.length || !TagResourceType[resourceType]) {
            return;
        }

        const resourceProperty: string = this.getMappingPropertyForResourceType(resourceType);

        mappings.forEach(mapping => {
            const matchingResource = resourcesMap.get(mapping[resourceProperty]);

            if (!matchingResource) {
                return;
            }

            if (!matchingResource?.TagIds) {
                matchingResource.TagIds = [];
            }

            matchingResource.TagIds.push(mapping.TagId);
        })

    }

    private getMappingPropertyForResourceType(resourceType: TagResourceType): string {
        switch (resourceType) {
            case TagResourceType.Driver: return "DriverId";
            case TagResourceType.Geofence: return "GeofenceId";
            case TagResourceType.Vehicle: return "VehicleId";
            default: return "";
        }
    }

    private getTagMappingCallForResourceType(resourceType: TagResourceType): any {
        switch (resourceType) {
            case TagResourceType.Driver: return () => this.getTagDriverMappings;
            case TagResourceType.Geofence: return () => this.getTagGeofenceMappings;
            case TagResourceType.Vehicle: return () => this.getTagVehicleMappings;
            default: return null;
        }
    }
}
